import { getUserContainer } from 'container/user-module'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { emptyUserDTO, UserDTO } from 'modules/users/models/User'
import React, { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppButton, ButtonTheme } from 'components/app-button/AppButton'
import styles from './MarketplaceTab.module.scss'
import genericStyle from '../../../../common/utils/generic.module.css'
import { Header } from 'components/header/Header'
import {
  Box,
  Button,
  Typography,
  Alert,
  Skeleton,
  Rating,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material'
import { IStatusService } from 'common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from 'container/app'
import AvatarProfile from 'components/avatar-profile/AvatarProfile'
import { Input } from 'pages/user-profile/userProfileStylesMui'
import { useIsOpenMenuLeftContext } from 'common/utils/isOpenMenuLeftContext'
import { useNavigate } from 'react-router-dom'
import { InputTextDescription } from 'features/resource/userProfileStylesMui'
import { reduceString } from 'common/utils/strings'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export function MarketplaceTab() {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [userToEdit, setUserToEdit] = useState<UserDTO>(emptyUserDTO())
  const [message, setMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const navigate = useNavigate()

  useEffect(() => {
    userService.getByID(loggedUser?.id || '').subscribe((res) => {
      res && setUserToEdit(res.toDTO())
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    setMessage('')
  }, [userToEdit])

  const isPatient = useMemo(() => {
    return userToEdit?.roles.includes('patient')
  }, [userToEdit])

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setUserToEdit(Object.assign({ ...userToEdit }, { [e.target.name]: e.target.value }))

  const handleSwitch = (e: ChangeEvent<HTMLInputElement>) => {
    setUserToEdit(Object.assign({ ...userToEdit }, { availability: !userToEdit.availability }))
  }

  const updateUserProfile = (e: FormEvent) => {
    e.preventDefault()
    userService.updateExternal(userToEdit).subscribe({
      next: () => statusService.sendStatus({ variant: 'success' }),
      error: () => setErrorMessage('error'),
    })
  }

  return (
    <Box className={`${genericStyle.container} ${styles.parentContainer}`}>
      {!isLoading ? (
        <form className={styles.form} onSubmit={updateUserProfile}>
          <Box display={'flex'} bgcolor="transparent" m="1rem" width={'100%'}>
            <Box className={styles.avatarContainer}>
              <AvatarProfile />
              <Rating
                name="read-only"
                value={userToEdit.averageRating || 0}
                precision={0.1}
                readOnly
              />
              <Tooltip title={`${userToEdit.firstName} ${userToEdit.lastName}`}>
                <Box className={styles.nameContainer}>
                  <Typography className={styles.userName}>
                    {reduceString(`${userToEdit.firstName} ${userToEdit.lastName}`, 80)}
                  </Typography>
                  <Typography className={styles.roleText}>{t('externProfessional')}</Typography>
                </Box>
              </Tooltip>
              <FormControlLabel
                control={
                  <Switch
                    checked={userToEdit.availability}
                    name="availability"
                    onChange={handleSwitch}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2e7161',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2e7161',
                      },
                    }}
                  />
                }
                label={t('acceptAppointments')}
              />
            </Box>
            <Box mt={4} width={'100%'}>
              <Box
                style={{ maxWidth: openMenuLeft ? '100%' : '77%' }}
                className={styles.headerContainer}
              >
                <Typography className={styles.headerTitle}>{t('userData')}</Typography>
              </Box>
              <Box mb={3} className={styles.textFieldBox}>
                <Box>
                  <Typography className={styles.inputTitle}>{t('professionalType')}</Typography>
                  <Input
                    id={'specialty'}
                    label=""
                    key={'specialty'}
                    name="specialty"
                    className={styles.textField}
                    value={userToEdit.specialty}
                    onChange={handleInput}
                    type="text"
                    variant={'outlined'}
                    size={'small'}
                  />
                </Box>
              </Box>

              <Box
                mb={3}
                style={{ maxWidth: openMenuLeft ? '100%' : '77%' }}
                className={styles.headerContainer}
              >
                <Typography className={styles.headerTitle} sx={{ marginTop: 4 }}>
                  {t('description')}
                </Typography>
              </Box>
              <Box mb={3} className={styles.inputTitle}>
                <Box style={{ width: '100%', minHeight: '100%' }}>
                  <InputTextDescription
                    fullWidth
                    multiline
                    id="biography"
                    name={'biography'}
                    value={userToEdit.biography}
                    label={''}
                    type={'text'}
                    onChange={handleInput}
                    rows={6}
                  />
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Box mb={3} />
                <Box className={styles.buttonContainer} display="flex">
                  <Button className={styles.buttonSubmit} type={'submit'} onClick={() => {}}>
                    {t('save')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          {message && (
            <Box mb={3}>
              <Alert severity="success" key="message" id="message">
                {t(message)}
              </Alert>
            </Box>
          )}
          {errorMessage && (
            <Box mb={3}>
              <Alert severity="error" key="errorMessage" id="errorMessage">
                {t(errorMessage)}
              </Alert>
            </Box>
          )}
        </form>
      ) : (
        <form onSubmit={updateUserProfile}>
          <Box bgcolor="white" m="1rem">
            <Box className={styles.headerContainer}>
              <Header label={t('userData')} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box style={{ borderBottom: '3px solid  #a9ba37' }}>
              <p className={styles.label}>{t('changePass')}</p>
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
            <Box className={styles.textFieldBox}>
              <Skeleton className={styles.textField} height={70} />
            </Box>
          </Box>
          <Box className={styles.buttonContainer} display="flex">
            <AppButton
              theme={ButtonTheme.NewPrimary}
              type={'submit'}
              label={t('save')}
              handler={() => {}}
            />
          </Box>
        </form>
      )}
    </Box>
  )
}
